/**
 * Deep-link preservation across the Keycloak round-trip.
 *
 * Callers put `{ returnTo }` in `signinRedirect({ state })`; OidcCallback
 * restores it after login. That is how the public calculator's
 * `/app/sendmoney?amount=500&to=CM` survives sign-in: ProtectedRoute starts
 * the redirect with the current path + query as `returnTo`.
 */

/** C0 controls, space and DEL. Browsers strip tabs/newlines from URLs, which can turn "/\t/x" into "//x". */
// eslint-disable-next-line no-control-regex
const UNSAFE_CHARS = /[\u0000- \u007f]/;

/**
 * Validates a `returnTo` value restored from OIDC state. Only same-origin
 * RELATIVE paths are allowed: must start with a single "/" (not "//", which
 * browsers treat as protocol-relative, an open redirect), contain no
 * backslashes (some browsers normalise "\" to "/") and no whitespace or
 * control characters. Anything else falls back to "/".
 */
export function safeReturnTo(value: unknown): string {
  if (
    typeof value === "string" &&
    value.startsWith("/") &&
    !value.startsWith("//") &&
    !value.includes("\\") &&
    !UNSAFE_CHARS.test(value)
  ) {
    return value;
  }
  return "/";
}

/** The `returnTo` for a router location: path + query + hash. */
export function returnToFor(location: { pathname: string; search?: string; hash?: string }): string {
  return safeReturnTo(`${location.pathname}${location.search ?? ""}${location.hash ?? ""}`);
}

/** Reads `returnTo` from the OIDC user state (unknown shape), validated. */
export function returnToFromState(state: unknown): string {
  if (state && typeof state === "object" && "returnTo" in state) {
    return safeReturnTo((state as { returnTo?: unknown }).returnTo);
  }
  return "/";
}

/**
 * sessionStorage copy of the last `returnTo`, for when the callback FAILS:
 * oidc-client-ts then has no user (so no `user.state`), and only an
 * authorization-server error carries the state back. "Try again" in
 * OidcCallback reads it so the deep link survives a failed login.
 */
export const RETURN_TO_STORAGE_KEY = "np.auth.returnTo";

/** The `signinRedirect` state for `returnTo`, also remembered for a failed callback. */
export function signinState(returnTo: string): { returnTo: string } {
  const value = safeReturnTo(returnTo);
  try {
    window.sessionStorage.setItem(RETURN_TO_STORAGE_KEY, value);
  } catch {
    // Storage blocked: the OIDC state still carries the deep link.
  }
  return { returnTo: value };
}

/** The remembered `returnTo`, validated ("/" when none). */
export function rememberedReturnTo(): string {
  try {
    return safeReturnTo(window.sessionStorage.getItem(RETURN_TO_STORAGE_KEY));
  } catch {
    return "/";
  }
}

/** Forgets the remembered `returnTo` once sign-in has completed. */
export function forgetReturnTo(): void {
  try {
    window.sessionStorage.removeItem(RETURN_TO_STORAGE_KEY);
  } catch {
    // Nothing to clean up.
  }
}

/**
 * `returnTo` for retrying a failed sign-in: the state an OIDC ErrorResponse
 * carries back (e.g. access_denied), else the remembered copy, else "/".
 */
export function returnToAfterError(error: unknown): string {
  const fromError = error && typeof error === "object" && "state" in error ? returnToFromState(error.state) : "/";
  return fromError !== "/" ? fromError : rememberedReturnTo();
}
